import type { CaregiverAvailability, TimeOffRequestView } from '@homecare/contracts';
import {
  Button,
  Card,
  EmptyState,
  Field,
  SelectField,
  Spinner,
  StatusPill,
  type StatusTone,
} from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * Weekly availability, time off and the next two weeks — one screen (slice-02
 * exit criterion 5).
 *
 * "Free in the next two weeks" is the API's `windows`: `resolveAvailability`
 * over the weekly pattern and **approved** time off only. A pending request is
 * listed with its Approve and Deny buttons and removes nothing until somebody
 * presses Approve — the screen shows exactly what the scheduler will read, so a
 * coordinator cannot be misled into booking around leave nobody granted.
 */

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_OFF_TONE: Record<TimeOffRequestView['status'], StatusTone> = {
  REQUESTED: 'progress',
  APPROVED: 'verified',
  DENIED: 'inactive',
  CANCELLED: 'inactive',
};

const TIME_OFF_LABEL: Record<TimeOffRequestView['status'], string> = {
  REQUESTED: 'Awaiting a decision',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  CANCELLED: 'Cancelled',
};

const TYPE_LABEL: Record<string, string> = {
  AVAILABLE: 'Available',
  PREFERRED: 'Prefers to work',
  UNAVAILABLE: 'Not available',
};

const day = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${iso}T00:00:00Z`));

export function AvailabilityCard({ caregiverId }: { caregiverId: string }): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const canRead = useHasPermission('caregiver:read');
  const canEdit = useHasPermission('caregiver:update');

  const [adding, setAdding] = useState<'rule' | 'time-off' | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const key = ['caregivers', 'availability', caregiverId];
  const availability = useQuery({
    queryKey: key,
    queryFn: () => api.get<CaregiverAvailability>(`/v1/caregivers/${caregiverId}/availability`),
    enabled: canRead,
    staleTime: 0,
  });

  if (!canRead) return null;

  /** Run a write, show its answer, and keep the error on screen if it fails. */
  const write = async (work: () => Promise<CaregiverAvailability>): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    try {
      queryClient.setQueryData(key, await work());
      return true;
    } catch (error) {
      setFailure(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const decide = (requestId: string, decision: 'APPROVED' | 'DENIED'): void => {
    void write(() =>
      api.send<CaregiverAvailability>(
        'POST',
        `/v1/caregivers/${caregiverId}/time-off/${requestId}/decision`,
        { decision },
      ),
    );
  };

  const removeRule = (ruleId: string): void => {
    void write(() =>
      api.send<CaregiverAvailability>(
        'POST',
        `/v1/caregivers/${caregiverId}/availability/${ruleId}/remove`,
        {},
      ),
    );
  };

  const data = availability.data;
  const windowsByDate = new Map<string, CaregiverAvailability['windows']>();
  for (const window of data?.windows ?? []) {
    windowsByDate.set(window.date, [...(windowsByDate.get(window.date) ?? []), window]);
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Availability and time off</h2>
          <p className="text-ink-3 mt-1 text-sm">
            When they work each week, the leave they have asked for, and what that leaves free.
            {data ? ` Times are ${data.timeZone.replace('_', ' ')}.` : ''}
          </p>
        </div>
        {canEdit && adding === null && (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAdding('rule')}>
              Add weekly hours
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setAdding('time-off')}>
              Record a time-off request
            </Button>
          </div>
        )}
      </div>

      <ErrorNote error={failure ?? availability.error} nextStep="Check the entry and try again." />

      {adding === 'rule' && (
        <RuleForm
          busy={busy}
          today={data?.from ?? ''}
          onCancel={() => setAdding(null)}
          onSubmit={async (body) => {
            const ok = await write(() =>
              api.send<CaregiverAvailability>(
                'POST',
                `/v1/caregivers/${caregiverId}/availability`,
                body,
              ),
            );
            if (ok) setAdding(null);
          }}
        />
      )}

      {adding === 'time-off' && (
        <TimeOffForm
          busy={busy}
          onCancel={() => setAdding(null)}
          onSubmit={async (body) => {
            const ok = await write(() =>
              api.send<CaregiverAvailability>(
                'POST',
                `/v1/caregivers/${caregiverId}/time-off`,
                body,
              ),
            );
            if (ok) setAdding(null);
          }}
        />
      )}

      {availability.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 py-4 text-sm" role="status">
          <Spinner /> Loading availability…
        </p>
      ) : data ? (
        <div className="mt-4 grid gap-6">
          <section aria-labelledby={`weekly-${caregiverId}`}>
            <h3 id={`weekly-${caregiverId}`} className="text-ink text-sm font-bold">
              Each week
            </h3>
            {data.rules.length === 0 ? (
              <EmptyState
                title="No weekly hours yet"
                explanation="Add the days and hours this caregiver works. Until then they have no availability, so nothing can be booked for them."
              />
            ) : (
              <ul className="mt-2 grid gap-1 text-sm">
                {data.rules.map((rule) => (
                  <li key={rule.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-ink">
                      {DAYS[rule.dayOfWeek - 1]} {rule.startTime}–{rule.endTime}
                      {rule.endTime <= rule.startTime ? ' (overnight)' : ''} ·{' '}
                      <span className="text-ink-2">{TYPE_LABEL[rule.type]}</span>
                      <span className="text-ink-3">
                        {' '}
                        · from {rule.effectiveFrom}
                        {rule.effectiveTo ? ` to ${rule.effectiveTo}` : ''}
                      </span>
                    </span>
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => removeRule(rule.id)}>
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby={`time-off-${caregiverId}`}>
            <h3 id={`time-off-${caregiverId}`} className="text-ink text-sm font-bold">
              Time off
            </h3>
            {data.timeOff.length === 0 ? (
              <p className="text-ink-3 mt-1 text-sm">No time off has been asked for.</p>
            ) : (
              <ul className="mt-2 grid gap-2 text-sm">
                {data.timeOff.map((request) => (
                  <li
                    key={request.id}
                    className="border-line flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                  >
                    <span className="grid gap-0.5">
                      <span className="text-ink font-medium">
                        {day(request.startDate)}
                        {request.endDate !== request.startDate
                          ? ` – ${day(request.endDate)}`
                          : ''}{' '}
                        · {request.type.toLowerCase()}
                      </span>
                      <span className="text-ink-3">
                        {request.status === 'REQUESTED'
                          ? 'Still on the schedule until someone approves it.'
                          : request.reviewedByName
                            ? `${TIME_OFF_LABEL[request.status]} by ${request.reviewedByName}.`
                            : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <StatusPill tone={TIME_OFF_TONE[request.status]}>
                        {TIME_OFF_LABEL[request.status]}
                      </StatusPill>
                      {canEdit && request.status === 'REQUESTED' && (
                        <>
                          <Button
                            size="sm"
                            pending={busy}
                            onClick={() => decide(request.id, 'APPROVED')}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => decide(request.id, 'DENIED')}
                          >
                            Deny
                          </Button>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby={`free-${caregiverId}`}>
            <h3 id={`free-${caregiverId}`} className="text-ink text-sm font-bold">
              Free from {day(data.from)} to {day(data.to)}
            </h3>
            {windowsByDate.size === 0 ? (
              <p className="text-ink-3 mt-1 text-sm">Not free at any time in these two weeks.</p>
            ) : (
              <ul className="mt-2 grid gap-1 text-sm" aria-label="Free times">
                {[...windowsByDate.entries()].map(([date, windows]) => (
                  <li key={date} className="text-ink">
                    <span className="inline-block w-28 font-medium">{day(date)}</span>
                    {windows
                      .map(
                        (window) =>
                          `${window.startTime}–${window.endTime === '24:00' ? 'midnight' : window.endTime}${window.preferred ? ' (preferred)' : ''}`,
                      )
                      .join(', ')}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </Card>
  );
}

function RuleForm({
  busy,
  today,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  // The agency's today, from the availability response. The browser's clock is
  // in the wrong time zone for this and the lint rule forbids reading it.
  today: string;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}): ReactNode {
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [type, setType] = useState('AVAILABLE');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void onSubmit({ dayOfWeek: Number(dayOfWeek), startTime, endTime, effectiveFrom, type });
  };

  return (
    <form
      onSubmit={submit}
      className="border-line mt-4 grid gap-3 rounded-md border p-4 sm:grid-cols-2"
    >
      <SelectField
        label="Day"
        value={dayOfWeek}
        onChange={(event) => setDayOfWeek(event.target.value)}
      >
        {DAYS.map((name, index) => (
          <option key={name} value={String(index + 1)}>
            {name}
          </option>
        ))}
      </SelectField>
      <SelectField label="Kind" value={type} onChange={(event) => setType(event.target.value)}>
        <option value="AVAILABLE">Available</option>
        <option value="PREFERRED">Prefers to work</option>
        <option value="UNAVAILABLE">Not available</option>
      </SelectField>
      <Field
        label="From"
        type="time"
        value={startTime}
        onChange={(event) => setStartTime(event.target.value)}
      />
      <Field
        label="Until"
        hint="Earlier than the start means it runs overnight."
        type="time"
        value={endTime}
        onChange={(event) => setEndTime(event.target.value)}
      />
      <Field
        label="Starting on"
        type="date"
        value={effectiveFrom}
        onChange={(event) => setEffectiveFrom(event.target.value)}
      />
      <div className="flex items-end gap-2">
        <Button type="submit" pending={busy}>
          Save weekly hours
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function TimeOffForm({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}): ReactNode {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState('VACATION');
  const [note, setNote] = useState('');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void onSubmit({ startDate, endDate: endDate || startDate, type, note: note.trim() || null });
  };

  return (
    <form
      onSubmit={submit}
      className="border-line mt-4 grid gap-3 rounded-md border p-4 sm:grid-cols-2"
    >
      <Field
        label="First day off"
        type="date"
        required
        value={startDate}
        onChange={(event) => setStartDate(event.target.value)}
      />
      <Field
        label="Last day off"
        hint="Leave blank for a single day."
        type="date"
        value={endDate}
        onChange={(event) => setEndDate(event.target.value)}
      />
      <SelectField label="Reason" value={type} onChange={(event) => setType(event.target.value)}>
        <option value="VACATION">Vacation</option>
        <option value="SICK">Sick</option>
        <option value="PERSONAL">Personal</option>
        <option value="FAMILY">Family</option>
        <option value="BEREAVEMENT">Bereavement</option>
        <option value="UNPAID">Unpaid</option>
        <option value="OTHER">Other</option>
      </SelectField>
      <Field
        label="Note (optional)"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <p className="text-ink-3 text-sm sm:col-span-2">
        Recorded as a request. It stays on the schedule until someone approves it.
      </p>
      <div className="flex gap-2">
        <Button type="submit" pending={busy}>
          Record the request
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
